import {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { AgGridReact } from "ag-grid-react";

import TablePagination from "./TablePagination";
import AuthContext from "../context/AuthContext";
import {
  buildDatacatalogColumnDefs,
  buildGpnTccColumnDefs,
  buildPmHierColumnDefs,
  isDatacatalogType,
  isGpnTccCatalogType,
  isPackingType,
} from "../helpers";
import Spinner from "./Spinner";
import { FilterChangedEvent } from "ag-grid-community";
import {
  retrieveValueFromLocalStorageObject,
  saveObjectsToLocalStorage,
} from "../helpers/local-storage.helper";
import { GPPersistentData } from "../types/persistent-page-filters";

const GPHTable = ({
  data,
  tableType,
  isFirstSearch,
}: {
  data: any[];
  tableType: string;
  isFirstSearch: boolean;
}) => {
  const authCtx = useContext(AuthContext);
  const hasPermission = authCtx?.hasPermission;

  const [rowData, setRowData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const [pageSize, setPageSize] = useState(() =>
    retrieveValueFromLocalStorageObject<GPPersistentData>(
      "gp",
      "lastTableTopFilters",
      "pageSize",
      10
    )
  );
  const [searchInput, setSearchInput] = useState(() =>
    retrieveValueFromLocalStorageObject<GPPersistentData>(
      "gp",
      "lastTableTopFilters",
      "searchInput",
      ""
    )
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [activeColumnDefs, setActiveColumnDefs] = useState<any[]>([]);
  const [isSearchOngoing, setIsSearchOngoing] = useState<boolean>(false);
  const [noRowsOverlayTemplate, setNoRowsOverlayTemplate] = useState<string>(
    "<span><b>Use the Filters Above to Display Some Results<b><span>"
  );

  useEffect(() => {
    if (isDatacatalogType(tableType))
      setActiveColumnDefs(buildDatacatalogColumnDefs(tableType, hasPermission));
    else if (isGpnTccCatalogType(tableType))
      setActiveColumnDefs(buildGpnTccColumnDefs(tableType, hasPermission));
    else if (isPackingType(tableType))
      setActiveColumnDefs(buildPmHierColumnDefs(tableType, hasPermission));
  }, [tableType]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      flex: 1,
      wrapText: true,
      wrapHeaderText: true,
      autoHeight: true,
      suppressMovable: true,
      icons: {
        sortAscending: "<i class='fa fa-sort-up'/>",
        sortDescending: "<i class='fa fa-sort-down'/>",
        sortUnSort: "<i class='fa fa-sort' style='color:#e3e6f0'></i>",
      },
    }),
    []
  );

  const onPageSizeChanged = useCallback((e: any) => {
    gridRef.current?.api.paginationSetPageSize(Number(e.target.value));
    setPageSize(Number(e.target.value));

    saveObjectsToLocalStorage<GPPersistentData>({
      prefix: "gp",
      lastTableTopFilters: {
        searchInput,
        pageSize: Number(e.target.value),
      },
    });
  }, []);

  const onPaginationChanged = useCallback(() => {
    if (gridRef.current?.api) {
      setPageSize(gridRef.current.api.paginationGetPageSize());
      setCurrentPage(gridRef.current.api.paginationGetCurrentPage() + 1);
      setTotalPages(gridRef.current.api.paginationGetTotalPages());
      setTotalEntries(gridRef.current.api.getDisplayedRowCount());
    }
  }, [gridRef]);

  const handleTableSearch = (searchInput: string) => {
    gridRef.current?.api.setQuickFilter(searchInput);
    setSearchInput(searchInput);

    saveObjectsToLocalStorage<GPPersistentData>({
      prefix: "gp",
      lastTableTopFilters: {
        searchInput,
        pageSize,
      },
    });
  };

  useEffect(() => {
    setRowData(data);

    //The code section below will eventually be replaced by the real loading of the products. This is just for the grid to rerender even when there are no results
    setIsSearchOngoing(true);
    setTimeout(() => {
      setIsSearchOngoing(false);
    }, 350);
  }, [data]);

  useEffect(() => {
    if (!isFirstSearch) {
      setNoRowsOverlayTemplate("<span><b>No Results Found<b><span>");
    } else {
      setNoRowsOverlayTemplate(
        "<span><b>Use the Filters Above to Display Some Results<b><span>"
      );
    }
  }, [isFirstSearch]);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    event.api?.getDisplayedRowCount() === 0
      ? event.api.showNoRowsOverlay()
      : event.api.hideOverlay();
  }, []);

  return (
    <>
      <div className="d-flex justify-content-between table-top-container">
        <div className="d-flex align-items-center">
          <label className="d-inline-block">
            Show
            <select
              onChange={onPageSizeChanged}
              className="table-top-input"
              id="page-size"
              value={pageSize}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            entries
          </label>
        </div>
        <div
          id="pp_search_filter"
          className="dataTables_filter table-top-search"
        >
          <label>
            Search:
            <input
              onChange={(e) => handleTableSearch(e.target.value.toLowerCase())}
              type="search"
              className="table-top-input"
              placeholder=""
              aria-controls="pp_search"
              value={searchInput}
            />
          </label>
        </div>
      </div>
      <div className="ag-theme-alpine">
        {!isFirstSearch && isSearchOngoing ? (
          <Spinner />
        ) : (
          <AgGridReact
            domLayout="autoHeight"
            ref={gridRef}
            rowData={rowData}
            columnDefs={activeColumnDefs}
            defaultColDef={defaultColDef}
            suppressRowClickSelection
            pagination={true}
            paginationPageSize={pageSize}
            onPaginationChanged={onPaginationChanged}
            unSortIcon
            suppressPaginationPanel
            overlayNoRowsTemplate={noRowsOverlayTemplate}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            onFilterChanged={onFilterChanged}
            tooltipShowDelay={0}
            onRowDataUpdated={() => handleTableSearch(searchInput)}
          ></AgGridReact>
        )}
      </div>
      <TablePagination
        gridRef={gridRef}
        pageSize={pageSize}
        currentPage={currentPage}
        totalPages={totalPages}
        totalEntries={totalEntries}
      />
    </>
  );
};

export default GPHTable;
